// Alteration tickets, shown in place of the style grid: the list on the left
// (every status, searchable by ticket, bill no., customer or mobile), the
// picked ticket or a new one on the right. A garment can be taken in from a
// past bill found by number or mobile, from the current bill, or with just the
// customer's details. Online only — tickets are shared by every till.
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import useFashionAlterations, { MIN_BILL_SEARCH, SEARCH_DELAY_MS } from "./hooks/useFashionAlterations";
import useFashionToast, { Toast } from "./hooks/useFashionToast";
import {
  ALTERATION_TYPES,
  MAX_WORK,
  applyOperations,
  hasWork,
  toggleWork,
  billLines,
  countByStatus,
  defaultPromisedDate,
  isOverdue,
  MAX_BILL_NO,
  MAX_NOTES,
  MAX_TAILOR,
  MOVES,
  STATUS_LABEL,
  STEPS,
  TABS,
  validateAlterationDraft,
} from "../../../utils/fashion/alterations";
import { formatMoney } from "../../../utils/fashion/money";
import { printAlterationSlip } from "../../../utils/fashion/alterationSlip";
import "./FashionWorkPanels.css";
import "./FashionAlterationsPanel.css";
import EmptyState from "./EmptyState";

const showDate = (d) => (d && dayjs(d).isValid() ? dayjs(d).format("ddd DD MMM") : d || "");
const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
const TONE = { RCV: "neu", INP: "warn", RDY: "ok", DLV: "info", CAN: "bad" };
// Names the store floor uses (v4 board); the server statuses are unchanged.
const ALT_LABEL = { ...STATUS_LABEL, RCV: "Open", INP: "In tailoring", RDY: "Ready", DLV: "Delivered" };
const MOVE_LABEL = { INP: "Send to tailor", DLV: "Mark delivered" };
const moveLabel = (from, m) => (from === "RDY" && m.to === "INP" ? "Back to tailor" : MOVE_LABEL[m.to] || m.label);
const tabLabel = (t) => (t.value === "ALL" ? t.label : ALT_LABEL[t.value] || t.label);
// "₹250.00" → "₹250" on a job chip.
const chipMoney = (v, tillData) => formatMoney(v, tillData).replace(/\.0+$/, "");
// Quick "Ready by" picks: days from today.
const READY_IN = [
  { days: 2, label: "In 2 days" },
  { days: 4, label: "In 4 days" },
  { days: 7, label: "In a week" },
];

function Pill({ status, dim }) {
  return <span className={`fp-wp-pill is-${TONE[status] || "neu"}${dim ? " is-dim" : ""}`}>{ALT_LABEL[status] || status}</span>;
}

function Detail({ alt, tillData, busy, confirming, setConfirming, onMove, say }) {
  const at = STEPS.indexOf(alt.status);
  const [printing, setPrinting] = useState(false);
  const printSlip = async () => {
    if (printing) return;
    setPrinting(true);
    const res = await printAlterationSlip(alt, tillData);
    setPrinting(false);
    if (typeof say === "function") say(res.ok ? `Slip for ${alt.documentNo} sent to the printer.` : res.message, res.ok ? "ok" : "bad");
  };
  return (
    <div className="fp-alt-detail" aria-label={`Alteration ${alt.documentNo}`}>
      <div className="fp-wp-head">
        <h3 className="fp-wp-h">{alt.documentNo}</h3>
        <Pill status={alt.status} />
        {isOverdue(alt) && <span className="fp-wp-pill is-bad">Overdue</span>}
        <button type="button" className="fp-btn-ghost fp-btn-sm fp-alt-slip" onClick={printSlip} disabled={printing}>
          {printing ? "Printing…" : "Print slip"}
        </button>
      </div>
      {alt.status !== "CAN" && (
        <ol className="fp-alt-steps" aria-label="Progress">
          {STEPS.map((s, i) => (
            <li key={s} className={`fp-wp-pill ${i < at ? "is-ok" : i === at ? `is-${TONE[s]}` : "is-neu is-dim"}`}>
              {ALT_LABEL[s]}
            </li>
          ))}
        </ol>
      )}
      <div className="fp-alt-cust">
        <span className="fp-wp-av" aria-hidden="true">
          {initials(alt.customerName)}
        </span>
        <span>
          <b>{alt.customerName || "Customer"}</b>
          <span className="fp-wp-meta fp-num">{alt.mobileNo}</span>
        </span>
      </div>
      <dl className="fp-alt-kv fp-num">
        <dt>Item</dt>
        <dd>{alt.productName || "—"}</dd>
        <dt>Work</dt>
        <dd>{alt.type}</dd>
        {alt.notes && (
          <>
            <dt>Note</dt>
            <dd>{alt.notes}</dd>
          </>
        )}
        {alt.tailor && (
          <>
            <dt>Tailor</dt>
            <dd>{alt.tailor}</dd>
          </>
        )}
        <dt>From bill</dt>
        <dd>{alt.orderDocumentNo || "—"}</dd>
        <dt>{alt.status === "DLV" ? "Delivered" : "Due"}</dt>
        <dd>{showDate(alt.status === "DLV" ? alt.deliveredAt : alt.promisedDate)}</dd>
        {alt.charge > 0 && (
          <>
            <dt>Charge</dt>
            <dd>{formatMoney(alt.charge, tillData)}</dd>
          </>
        )}
      </dl>
      {(MOVES[alt.status] || []).length > 0 ? (
        <div className="fp-alt-acts">
          {MOVES[alt.status].map((m, i) => (
            <button
              key={m.to}
              type="button"
              className={i === 0 ? "fp-btn-primary" : "fp-btn-ghost"}
              disabled={busy}
              onClick={() => onMove(alt, m.to)}
              onBlur={m.to === "CAN" ? () => setConfirming((c) => (c === alt.id ? null : c)) : undefined}
            >
              {m.to === "CAN" && confirming === alt.id ? "Confirm cancel" : moveLabel(alt.status, m)}
            </button>
          ))}
        </div>
      ) : (
        <p className="fp-wp-meta">{alt.status === "DLV" ? "Handed over. Nothing left to do." : "Cancelled. Nothing left to do."}</p>
      )}
    </div>
  );
}

// The new-ticket form. source is where the garment came from: a past bill
// (with its items), the bill on the till, or none (customer details only).
function AlterationForm({ tillData, cart, customer, namedCustomer, startBill, startItemId, findBills, billItems, options, create, say, onDone, onTaken }) {
  const cartLines = billLines(cart);
  const [source, setSource] = useState(() => (startBill ? { kind: "bill", bill: startBill, lines: null } : cartLines.length ? { kind: "cart" } : { kind: "none" }));
  const [bq, setBq] = useState("");
  const [hits, setHits] = useState(null);
  const [draft, setDraft] = useState(() => ({
    customerName: startBill ? startBill.customerName : namedCustomer ? customer?.name || "" : "",
    mobileNo: startBill ? startBill.mobileNo : namedCustomer ? customer?.mobileNo || "" : "",
    itemId: startBill ? "" : cartLines.some((l) => l.productId === startItemId) ? startItemId : cartLines.length === 1 ? cartLines[0].productId : "",
    billNo: "",
    alterationType: "",
    notes: "",
    tailor: "",
    tailorId: "",
    charge: "",
    promisedDate: defaultPromisedDate(),
  }));
  const [saving, setSaving] = useState(false);
  // The store's priced jobs and tailors; none (typed by hand) until read or when the read fails.
  const [choices, setChoices] = useState({ operations: [], tailors: [] });
  const [pickedOps, setPickedOps] = useState([]);
  const set = (field) => (e) => setDraft((d) => ({ ...d, [field]: e.target.value }));
  const bill = source.kind === "bill" ? source.bill : null;
  const lines = source.kind === "bill" ? source.lines || [] : source.kind === "cart" ? cartLines : [];

  // The picked bill's items, read once.
  const billId = bill?.orderId;
  useEffect(() => {
    if (!billId) return undefined;
    let live = true;
    billItems(billId).then((items) => {
      if (!live) return;
      if (items === null) say("Couldn't read the items on that bill. Pick it again or take the garment in without a bill.", "bad");
      setSource((s) => (s.kind === "bill" && s.bill.orderId === billId ? { ...s, lines: items || [] } : s));
      if (items?.length === 1) setDraft((d) => ({ ...d, itemId: items[0].productId }));
    });
    return () => {
      live = false;
    };
  }, [billId, billItems, say]);

  useEffect(() => {
    let live = true;
    Promise.resolve(options?.())
      .then((o) => live && o && setChoices({ operations: o.operations || [], tailors: o.tailors || [] }))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [options]);

  // Tapping a job fills the work and the charge from every picked job; both can still be edited by hand.
  const toggleOp = (id) => {
    const next = pickedOps.includes(id) ? pickedOps.filter((x) => x !== id) : [...pickedOps, id];
    const { alterationType, charge } = applyOperations(choices.operations, next);
    setPickedOps(next);
    setDraft((d) => ({ ...d, alterationType, charge: next.length ? String(charge) : "" }));
  };

  // Bill search, as the cashier types.
  useEffect(() => {
    const text = bq.trim();
    if (text.length < MIN_BILL_SEARCH) {
      setHits(null);
      return undefined;
    }
    let live = true;
    const timer = setTimeout(async () => {
      const found = await findBills(text);
      if (live) setHits(found);
    }, SEARCH_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [bq, findBills]);

  const pickBill = (b) => {
    setSource({ kind: "bill", bill: b, lines: null });
    setDraft((d) => ({ ...d, itemId: "", customerName: b.customerName || d.customerName, mobileNo: b.mobileNo || d.mobileNo }));
    setBq("");
    setHits(null);
  };
  const pickSource = (kind) => {
    setSource({ kind });
    setDraft((d) => ({ ...d, itemId: kind === "cart" && cartLines.length === 1 ? cartLines[0].productId : "" }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (bill && source.lines === null) {
      say("Still reading the bill's items. Try again in a moment.", "info");
      return;
    }
    const customerId = source.kind !== "bill" && namedCustomer ? customer?.cwrCustomerId : null;
    const { alteration, error } = validateAlterationDraft({ ...draft, customerId }, source.kind === "cart" ? cart : null, new Date(), bill ? { ...bill, lines } : null);
    if (error) {
      say(error, "warn");
      return;
    }
    setSaving(true);
    try {
      const res = await create(alteration);
      if (res.ok && source.kind === "cart" && alteration.productId) onTaken?.({ orderId: cart?.sOrderID || null, productId: alteration.productId, type: alteration.alterationType, documentNo: res.documentNo || "" });
      if (res.ok) onDone(res.documentNo || "", `Took in ${res.documentNo || "the alteration"}: ${alteration.alterationType}, ready by ${showDate(alteration.promisedDate)}.`);
      else say(res.message, "bad");
    } catch {
      say("Couldn't save the alteration. Check the connection and try again.", "bad");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="fp-alt-form" onSubmit={save} aria-label="New alteration">
      <div className="fp-wp-head">
        <h3 className="fp-wp-h">New alteration</h3>
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => onDone(null)} disabled={saving}>
          Cancel
        </button>
      </div>

      <div className="fp-alt-fl">
        <span className="fp-wp-lbl">1 · Find the bill</span>
        <div className="fp-wp-seg" role="group" aria-label="Where it was bought">
          <button type="button" className={source.kind === "bill" || source.kind === "find" ? "is-on" : ""} aria-pressed={source.kind === "bill" || source.kind === "find"} onClick={() => !bill && setSource({ kind: "find" })}>
            Past bill
          </button>
          {cartLines.length > 0 && (
            <button type="button" className={source.kind === "cart" ? "is-on" : ""} aria-pressed={source.kind === "cart"} onClick={() => pickSource("cart")}>
              This bill
            </button>
          )}
          <button type="button" className={source.kind === "none" ? "is-on" : ""} aria-pressed={source.kind === "none"} onClick={() => pickSource("none")}>
            No bill
          </button>
        </div>
      </div>

      {(source.kind === "find" || source.kind === "bill") && (
        <div className="fp-alt-fl">
          {bill ? (
            <div className="fp-alt-picked">
              <span>
                <b className="fp-num">{bill.documentNo}</b>
                <span className="fp-wp-meta">{[bill.customerName, bill.mobileNo, showDate(bill.date)].filter(Boolean).join(" · ")}</span>
              </span>
              <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => setSource({ kind: "find" })}>
                Change
              </button>
            </div>
          ) : (
            <>
              <label className="fp-wp-lbl" htmlFor="fp-alt-bq">
                Find the bill
              </label>
              <input id="fp-alt-bq" className="fp-input fp-num" value={bq} onChange={(e) => setBq(e.target.value)} placeholder="Bill no. or mobile" autoComplete="off" />
              {hits === null && bq.trim().length >= MIN_BILL_SEARCH ? null : hits && hits.length === 0 ? (
                <span className="fp-wp-meta">No bill matches. Try the full bill number or the mobile on the bill.</span>
              ) : (
                (hits || []).map((b) => (
                  <button key={b.orderId} type="button" className="fp-alt-hit" onClick={() => pickBill(b)}>
                    <b className="fp-num">{b.documentNo}</b>
                    <span className="fp-wp-meta">{[b.customerName, b.mobileNo, showDate(b.date)].filter(Boolean).join(" · ")}</span>
                  </button>
                ))
              )}
            </>
          )}
        </div>
      )}

      {source.kind !== "find" && (
        <>
          {(source.kind === "cart" || (bill && source.lines !== null)) && lines.length > 0 && (
            <div className="fp-alt-fl">
              <span className="fp-wp-lbl">2 · Which item</span>
              <div className="fp-wp-seg is-wrap" role="group" aria-label="Which item">
                {lines.map((l) => (
                  <button key={l.productId} type="button" className={draft.itemId === l.productId ? "is-on" : ""} aria-pressed={draft.itemId === l.productId} onClick={() => setDraft((d) => ({ ...d, itemId: l.productId }))}>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {bill && source.lines === null && <span className="fp-wp-meta">Reading the bill's items…</span>}

          <div className="fp-alt-grid">
            <label className="fp-field">
              <span className="fp-wp-lbl">Customer name</span>
              <input className="fp-input" value={draft.customerName} onChange={set("customerName")} autoComplete="off" />
            </label>
            <label className="fp-field">
              <span className="fp-wp-lbl">Mobile</span>
              <input className="fp-input fp-num" value={draft.mobileNo} onChange={set("mobileNo")} inputMode="tel" autoComplete="off" />
            </label>
            {source.kind === "none" && (
              <label className="fp-field">
                <span className="fp-wp-lbl">Bill no. (optional)</span>
                <input className="fp-input fp-num" value={draft.billNo} onChange={set("billNo")} maxLength={MAX_BILL_NO} autoComplete="off" />
              </label>
            )}
          </div>

          <div className="fp-alt-fl">
            <label className="fp-wp-lbl" htmlFor="fp-alt-type">
              {lines.length > 0 && source.kind !== "none" ? "3 · Work" : "Work"}
            </label>
            {choices.operations.length > 0 ? (
              <div className="fp-wp-seg is-wrap" role="group" aria-label="Alteration jobs">
                {choices.operations.map((op) => (
                  <button key={op.id} type="button" className={pickedOps.includes(op.id) ? "is-on" : ""} aria-pressed={pickedOps.includes(op.id)} onClick={() => toggleOp(op.id)}>
                    {`${op.name} · ${chipMoney(op.rate, tillData)}`}
                  </button>
                ))}
              </div>
            ) : (
              <div className="fp-wp-seg is-wrap" role="group" aria-label="Common alterations">
                {ALTERATION_TYPES.map((t) => (
                  <button key={t} type="button" className={hasWork(draft.alterationType, t) ? "is-on" : ""} aria-pressed={hasWork(draft.alterationType, t)} onClick={() => setDraft((d) => ({ ...d, alterationType: toggleWork(d.alterationType, t) }))}>
                    {t}
                  </button>
                ))}
              </div>
            )}
            <input id="fp-alt-type" className="fp-input" value={draft.alterationType} onChange={set("alterationType")} maxLength={MAX_WORK} placeholder={choices.operations.length > 0 ? "Pick one or more, or type it" : "Pick one or more, or type it, e.g. Hem + Taper"} autoComplete="off" />
          </div>
          <label className="fp-field">
            <span className="fp-wp-lbl">Measurement or note</span>
            <input className="fp-input" value={draft.notes} onChange={set("notes")} maxLength={MAX_NOTES} placeholder="e.g. 3 cm, keep original hem" autoComplete="off" />
          </label>
          {choices.tailors.length > 0 ? (
            <label className="fp-field">
              <span className="fp-wp-lbl">Tailor (optional)</span>
              <select className="fp-input" value={draft.tailorId} onChange={set("tailorId")}>
                <option value="">No tailor yet</option>
                {choices.tailors.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="fp-field">
              <span className="fp-wp-lbl">Tailor (optional)</span>
              <input className="fp-input" value={draft.tailor} onChange={set("tailor")} maxLength={MAX_TAILOR} placeholder="Who will do the work" autoComplete="off" />
            </label>
          )}
          <div className="fp-alt-grid">
            <label className="fp-field">
              <span className="fp-wp-lbl">Ready by</span>
              <input className="fp-input fp-num" type="date" value={draft.promisedDate} min={dayjs().format("YYYY-MM-DD")} onChange={set("promisedDate")} />
            </label>
            <div className="fp-wp-seg is-wrap fp-alt-when" role="group" aria-label="Quick ready-by dates">
              {READY_IN.map((r) => {
                const d = dayjs().add(r.days, "day");
                const v = d.format("YYYY-MM-DD");
                return (
                  <button key={r.days} type="button" className={draft.promisedDate === v ? "is-on" : ""} aria-pressed={draft.promisedDate === v} onClick={() => setDraft((x) => ({ ...x, promisedDate: v }))}>
                    {`${r.label} · ${d.format("ddd DD MMM")}`}
                  </button>
                );
              })}
            </div>
            <label className="fp-field">
              <span className="fp-wp-lbl">Charge</span>
              <input className="fp-input fp-num" value={draft.charge} onChange={set("charge")} inputMode="decimal" placeholder="0" />
            </label>
          </div>
          <p className="fp-wp-meta">The charge is noted on the ticket. Collect it on a bill as usual.</p>
          <button type="submit" className="fp-btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Create alteration"}
          </button>
        </>
      )}
    </form>
  );
}

// startItemId: a bag line's product; the panel then opens on a new ticket for it.
export default function FashionAlterationsPanel({ tillData, isOnline, cart, customer, namedCustomer, startItemId = null, onClose, onTaken, backLabel = "Back to styles" }) {
  const [tab, setTab] = useState("ALL");
  const [term, setTerm] = useState("");
  const [side, setSide] = useState(() => (startItemId ? { mode: "new", id: null, bill: null, key: "line", itemId: startItemId } : { mode: "view", id: null, bill: null }));
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(null);
  const [toast, say] = useFashionToast();
  const { rows, bills, status, online, reload, create, move, billItems, findBills, options } = useFashionAlterations(tillData, isOnline, term.trim());

  const counts = countByStatus(rows);
  const tabMatch = (TABS.find((t) => t.value === tab) || TABS[0]).match;
  const shown = rows.filter((r) => tabMatch(r.status));
  const picked = side.mode === "view" ? rows.find((r) => (side.id ? r.id === side.id : side.docNo && r.documentNo === side.docNo)) || null : null;
  const q = term.trim();
  // Bills that matched but have no ticket yet: the cashier can start one.
  const ticketBills = new Set(rows.map((r) => r.orderDocumentNo).filter(Boolean));
  const freeBills = q ? (bills || []).filter((b) => !ticketBills.has(b.documentNo)) : [];

  const doMove = async (alt, to) => {
    if (to === "CAN" && confirming !== alt.id) {
      setConfirming(alt.id);
      return;
    }
    setConfirming(null);
    setBusy(alt.id);
    try {
      const res = await move(alt.id, to);
      say(res.ok ? `${alt.documentNo || "Alteration"}: ${ALT_LABEL[to].toLowerCase()}.` : res.message, res.ok ? "ok" : "bad");
    } catch {
      say("Couldn't update the alteration. Check the connection and try again.", "bad");
    } finally {
      setBusy(null);
    }
  };

  const startNew = (bill = null) => setSide({ mode: "new", id: null, bill, key: Date.now() });

  return (
    <section className="fp-panel fp-alt" aria-label="Alterations">
      <div className="fp-panel-head">
        <h2 className="fp-panel-title">Alterations</h2>
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={onClose}>
          {backLabel}
        </button>
      </div>
      <Toast toast={toast} />
      {!online ? (
        <EmptyState icon="offline" title="You're offline" hint="Alterations need a connection, because every till in the store shares them. Reconnect the till and try again." />
      ) : (
        <div className="fp-alt-split">
          <div className="fp-alt-main">
            <div className="fp-alt-bar">
              <label className="fp-alt-search">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.5-3.5" />
                </svg>
                <input className="fp-num" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Bill no., mobile or customer" aria-label="Search alterations" />
              </label>
              <button type="button" className="fp-btn-primary" onClick={() => startNew()}>
                New alteration
              </button>
            </div>

            <div className="fp-alt-stats fp-num" aria-label="Summary">
              <div>
                <span>Open</span>
                <b>{counts.RCV}</b>
              </div>
              <div>
                <span>In tailoring</span>
                <b>{counts.INP}</b>
              </div>
              <div>
                <span>Ready for pickup</span>
                <b>{counts.RDY}</b>
              </div>
              <div>
                <span>Delivered</span>
                <b>{counts.DLV}</b>
              </div>
            </div>

            <div className="fp-wp-card">
              <div className="fp-wp-head">
                <div className="fp-wp-seg is-scroll" role="group" aria-label="Show">
                  {TABS.map((t) => (
                    <button key={t.value} type="button" className={tab === t.value ? "is-on" : ""} aria-pressed={tab === t.value} onClick={() => setTab(t.value)}>
                      {t.value === "ALL" ? t.label : `${tabLabel(t)} · ${counts[t.value]}`}
                    </button>
                  ))}
                </div>
                <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => reload()} disabled={status === "loading"}>
                  Refresh
                </button>
              </div>
              {status === "error" && <p className="fp-wp-meta is-bad">Couldn't load every alteration. The list may be out of date — tap Refresh.</p>}
              {shown.length > 0 && (
                <div className="fp-alt-item fp-alt-th" aria-hidden="true">
                  <span>Ref</span>
                  <span>Customer</span>
                  <span>Item · work</span>
                  <span>Bill no</span>
                  <span>Due</span>
                  <span>Status</span>
                </div>
              )}
              <ul className="fp-alt-list" aria-busy={status === "loading" ? "true" : "false"}>
                {shown.length === 0 &&
                  (status === "loading" || status === "idle" ? (
                    <li className="fp-empty">Loading alterations…</li>
                  ) : q ? (
                    <EmptyState as="li" icon="search" title={`No alteration matches “${q}” in any status.`} hint="Check the spelling, or try another search." />
                  ) : tab === "ALL" ? (
                    <EmptyState as="li" title="No alterations yet." hint="Tap New alteration to book one." />
                  ) : (
                    <EmptyState as="li" title="No alterations with this status." />
                  ))}
                {shown.map((alt) => (
                  <li key={alt.id}>
                    <button type="button" className={`fp-alt-item${picked?.id === alt.id ? " is-on" : ""}`} aria-pressed={picked?.id === alt.id} onClick={() => setSide({ mode: "view", id: alt.id, bill: null })}>
                      <span className="fp-alt-ref fp-num">{alt.documentNo}</span>
                      <span className="fp-alt-who">
                        {alt.customerName || "—"}
                        <span className="fp-wp-meta fp-num">{alt.mobileNo}</span>
                      </span>
                      <span className="fp-alt-what">
                        {alt.productName || alt.type}
                        <span className="fp-wp-meta">{[alt.productName ? alt.type : "", alt.tailor ? `Tailor: ${alt.tailor}` : ""].filter(Boolean).join(" · ")}</span>
                      </span>
                      <span className="fp-alt-bill fp-num">{alt.orderDocumentNo || ""}</span>
                      <span className="fp-alt-due fp-num">{showDate(alt.status === "DLV" ? alt.deliveredAt : alt.promisedDate)}</span>
                      <span className="fp-alt-st">
                        <Pill status={alt.status} />
                        {isOverdue(alt) && <span className="fp-wp-pill is-bad">Overdue</span>}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {freeBills.length > 0 && (
                <div className="fp-alt-bills" aria-label="Bills found">
                  <span className="fp-wp-lbl">Bills with no alteration yet</span>
                  {freeBills.map((b) => (
                    <div key={b.orderId} className="fp-alt-picked">
                      <span>
                        <b className="fp-num">{b.documentNo}</b>
                        <span className="fp-wp-meta">{[b.customerName, b.mobileNo, showDate(b.date)].filter(Boolean).join(" · ")}</span>
                      </span>
                      <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => startNew(b)}>
                        Take in from this bill
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="fp-alt-side" aria-label="Alteration detail">
            {side.mode === "new" ? (
              <AlterationForm
                key={side.key}
                tillData={tillData}
                cart={cart}
                customer={customer}
                namedCustomer={namedCustomer}
                startBill={side.bill}
                startItemId={side.itemId}
                findBills={findBills}
                billItems={billItems}
                options={options}
                create={create}
                say={say}
                onTaken={onTaken}
                onDone={(docNo, text) => {
                  if (text) say(text, "ok");
                  setTab("ALL");
                  setSide({ mode: "view", id: null, bill: null, docNo });
                }}
              />
            ) : picked ? (
              <Detail alt={picked} tillData={tillData} busy={busy === picked.id} confirming={confirming} setConfirming={setConfirming} onMove={doMove} say={say} />
            ) : (
              <EmptyState icon="pick" title="Pick an alteration" hint="Its details and next step show here." />
            )}
          </aside>
        </div>
      )}
    </section>
  );
}
